import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions, Permission } from '../hooks/usePermissions';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  fallback?: string;
}

/**
 * Route guard component that checks permissions before rendering children
 *
 * @example
 * <ProtectedRoute permission={Permission.PRODUCT_CREATE}>
 *   <AddProductPage />
 * </ProtectedRoute>
 */
export const ProtectedRoute = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback,
}: ProtectedRouteProps) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAuthenticated, getDefaultRoute } = usePermissions();
  const resolvedFallback = fallback ?? getDefaultRoute();
  const location = useLocation();

  if (!isAuthenticated) {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect_to=${redirectTo}`} replace />;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return <Navigate to={resolvedFallback} replace />;
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return <Navigate to={resolvedFallback} replace />;
    }
  }

  return <>{children}</>;
};
